import axios from 'axios';
import { updateLoggedInStatus } from '../actions/loginActions';
import { notificationMessage } from '../actions/notificationMessageActions';
import { setNetworkError } from '../actions/siteActions';
import { getEmployerAccountDetails } from '../actions/userActions';
import * as actions from '../constants/action-types';
import {
    API_URL,
    GET_TAGS_LIST,
    POST_CREATE_TAG_URL,
    POST_DELETE_TAG_URL,
    POST_ORG_LOGO_UPLOAD_URL,
    POST_ORG_LOGO_DELETE_URL,
    POST_ORG_CUSTOM_COLORS_URL,
} from '../constants/api';
const config = {
    withCredentials: true,
};

export const getSettings =
    ({ url }) =>
    async (dispatch) => {
        dispatch({
            type: actions.LOADING_SETTINGS,
            payload: {
                loading: true,
            },
        });

        try {
            const { data } = await axios.get(url, config);

            if (data.status === 'success') {
                dispatch({
                    type: actions.RECEIVED_SETTINGS,
                    payload: data.data,
                });
            } else {
                dispatch(
                    notificationMessage({
                        type: 'error',
                        messageArray: Object.values(data.data),
                    })
                );
            }
        } catch (error) {
            dispatch(
                notificationMessage({
                    type: 'error',
                    message: error.message,
                    timeout: 8000,
                })
            );
        } finally {
            dispatch({
                type: actions.LOADING_SETTINGS,
                payload: {
                    loading: false,
                },
            });
        }
    };

export const updateSettings =
    ({ url, settings }) =>
    async (dispatch) => {
        dispatch({
            type: actions.UPDATE_SETTINGS,
            payload: {
                updating: true,
            },
        });

        try {
            const params = new URLSearchParams(settings);
            const { data } = await axios.post(url, params, config);

            if (data.status === 'success') {
                dispatch({
                    type: actions.RECEIVED_SETTINGS,
                    payload: settings,
                });
                dispatch(
                    notificationMessage({
                        type: 'success',
                        message: 'Successfully updated settings',
                    })
                );
            } else {
                dispatch(
                    notificationMessage({
                        type: 'error',
                        messageArray: Object.values(data.data),
                    })
                );
            }
        } catch (error) {
            dispatch(setNetworkError(error));
        } finally {
            dispatch({
                type: actions.UPDATE_SETTINGS,
                payload: {
                    updating: false,
                },
            });
        }
    };

export const createOrgTag = (tag_name, tag_hex_colour_bg, tag_hex_colour_text) => (dispatch) => {
    const url = API_URL + POST_CREATE_TAG_URL;
    const params = new URLSearchParams();
    const config = { withCredentials: true };

    // Add parameters to the request
    params.append('tag_name', tag_name);
    params.append('tag_hex_colour_bg', tag_hex_colour_bg);
    params.append('tag_hex_colour_text', tag_hex_colour_text);

    // Dispatch the action to indicate we're creating a tag
    dispatch({
        type: actions.CREATE_ORG_TAG,
        payload: {
            tag_name,
            tag_hex_colour_bg,
            tag_hex_colour_text,
        },
    });

    // Make the API request
    axios
        .post(url, params, config)
        .then(({ data }) => {
            if (data.status === 'success') {
                dispatch(updateLoggedInStatus(data));
                dispatch(createdOrgTag(data.data));
                dispatch(
                    notificationMessage({
                        type: 'success',
                        textKey: 'Settings.organisation.tagCreatedSuccess',
                    })
                );
                dispatch(getTagsList());
            } else {
                dispatch(createOrgTagFailed());
            }
        })
        .catch((error) => {
            dispatch(setNetworkError(error));
            dispatch(createOrgTagFailed());
        });
};

const createdOrgTag = (tag) => ({
    type: actions.CREATE_ORG_TAG_SUCCESS,
    payload: {
        tag,
    },
});

const createOrgTagFailed = () => (dispatch) => {
    dispatch(
        notificationMessage({
            type: 'error',
            textKey: 'Settings.organisation.tagCreatedError',
        })
    );
    dispatch({ type: actions.CREATE_ORG_TAG_FAILED });
};

export const deleteOrgTag = (tagId) => (dispatch) => {
    const url = API_URL + POST_DELETE_TAG_URL;
    const params = new URLSearchParams();
    const config = { withCredentials: true };

    // Add parameters to the request
    params.append('tag_id', tagId);

    // Dispatch the action to indicate we're deleting a tag
    dispatch({
        type: actions.DELETE_ORG_TAG,
        payload: {
            tagId,
        },
    });

    // Make the API request
    axios
        .post(url, params, config)
        .then(({ data }) => {
            if (data.status === 'success') {
                dispatch(updateLoggedInStatus(data));
                dispatch(deletedOrgTag(tagId));
                dispatch(
                    notificationMessage({
                        type: 'success',
                        textKey: 'Settings.organisation.tagDeletedSuccess',
                    })
                );
                dispatch(getTagsList());
            } else {
                dispatch(deleteOrgTagFailed());
            }
        })
        .catch((error) => {
            dispatch(setNetworkError(error));
            dispatch(deleteOrgTagFailed());
        });
};

const deletedOrgTag = (tagId) => ({
    type: actions.DELETE_ORG_TAG_SUCCESS,
    payload: {
        tagId,
    },
});

const deleteOrgTagFailed = () => (dispatch) => {
    dispatch(
        notificationMessage({
            type: 'error',
            textKey: 'Settings.organisation.tagDeletedError',
        })
    );
    dispatch({ type: actions.DELETE_ORG_TAG_FAILED });
};

export const getTagsList = () => (dispatch) => {
    const url = API_URL + GET_TAGS_LIST;
    const config = { withCredentials: true };

    // Dispatch the action to indicate we're fetching tags
    dispatch({
        type: actions.GET_TAGS_LIST,
    });

    // Make the API request
    axios
        .get(url, config)
        .then(({ data }) => {
            if (data.status === 'success') {
                dispatch(updateLoggedInStatus(data));
                dispatch(receivedTagsList(data.data));
            } else {
                dispatch(getTagsListError());
            }
        })
        .catch((error) => {
            dispatch(setNetworkError(error));
            dispatch(getTagsListError());
        });
};

const receivedTagsList = (tags) => ({
    type: actions.RECEIVED_TAGS_LIST,
    payload: {
        tags,
    },
});

const getTagsListError = () => (dispatch) => {
    dispatch(
        notificationMessage({
            type: 'error',
            textKey: 'Settings.organisation.tags.fetchError',
        })
    );
    dispatch({ type: actions.GET_TAGS_LIST_ERROR });
};

export const uploadOrgLogo = (file) => (dispatch) => {
    const url = API_URL + POST_ORG_LOGO_UPLOAD_URL;
    const params = new FormData();
    const config = {
        withCredentials: true,
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    };

    params.append('file', file);

    dispatch({ type: actions.UPLOAD_ORG_LOGO });

    axios
        .post(url, params, config)
        .then(({ data }) => {
            dispatch(updateLoggedInStatus(data));

            if (data.status === 'success') {
                dispatch(uploadOrgLogoSuccess(data.data.logo_url));
                dispatch(
                    notificationMessage({
                        type: 'success',
                        textKey: 'Settings.organisation.logo.uploadSuccess',
                    })
                );
                // Refresh account details to get updated logo_url
                dispatch(getEmployerAccountDetails());
            } else {
                const errorMessage =
                    data.data?.file || data.message || 'Settings.organisation.logo.uploadError';
                dispatch(uploadOrgLogoFailed(errorMessage));
            }
        })
        .catch((error) => {
            dispatch(setNetworkError(error));
            dispatch(uploadOrgLogoFailed('Settings.organisation.logo.uploadError'));
        });
};

const uploadOrgLogoSuccess = (logoUrl) => ({
    type: actions.UPLOAD_ORG_LOGO_SUCCESS,
    payload: {
        logoUrl,
    },
});

const uploadOrgLogoFailed = (errorMessage) => (dispatch) => {
    dispatch(
        notificationMessage({
            type: 'error',
            textKey: typeof errorMessage === 'string' && errorMessage.startsWith('Settings.')
                ? errorMessage
                : 'Settings.organisation.logo.uploadError',
            message: typeof errorMessage === 'string' && !errorMessage.startsWith('Settings.')
                ? errorMessage
                : undefined,
        })
    );
    dispatch({ type: actions.UPLOAD_ORG_LOGO_FAILED });
};

export const deleteOrgLogo = () => (dispatch) => {
    const url = API_URL + POST_ORG_LOGO_DELETE_URL;
    const params = new URLSearchParams();
    const config = {
        withCredentials: true,
    };

    dispatch({ type: actions.DELETE_ORG_LOGO });

    axios
        .post(url, params, config)
        .then(({ data }) => {
            dispatch(updateLoggedInStatus(data));

            if (data.status === 'success') {
                dispatch(deleteOrgLogoSuccess());
                dispatch(
                    notificationMessage({
                        type: 'success',
                        textKey: 'Settings.organisation.logo.deleteSuccess',
                    })
                );
                // Refresh account details to get updated logo_url
                dispatch(getEmployerAccountDetails());
            } else {
                dispatch(deleteOrgLogoFailed(data.message));
            }
        })
        .catch((error) => {
            dispatch(setNetworkError(error));
            dispatch(deleteOrgLogoFailed('Settings.organisation.logo.deleteError'));
        });
};

const deleteOrgLogoSuccess = () => ({
    type: actions.DELETE_ORG_LOGO_SUCCESS,
});

const deleteOrgLogoFailed = (errorMessage) => (dispatch) => {
    dispatch(
        notificationMessage({
            type: 'error',
            textKey: typeof errorMessage === 'string' && errorMessage.startsWith('Settings.')
                ? errorMessage
                : 'Settings.organisation.logo.deleteError',
            message: typeof errorMessage === 'string' && !errorMessage.startsWith('Settings.')
                ? errorMessage
                : undefined,
        })
    );
    dispatch({ type: actions.DELETE_ORG_LOGO_FAILED });
};

export const updateOrgCustomColors = (linkColor, btnColor) => (dispatch) => {
    const url = API_URL + POST_ORG_CUSTOM_COLORS_URL;
    const params = new FormData();
    const config = {
        withCredentials: true,
    };

    // Normalize and add link color (send empty string to clear)
    const normalizedLinkColor = 
        !linkColor || linkColor.trim() === '' 
            ? '' 
            : linkColor.startsWith('#') 
                ? linkColor 
                : `#${linkColor}`;
    params.append('custom_link_hex', normalizedLinkColor);

    // Normalize and add button color (send empty string to clear)
    const normalizedBtnColor = 
        !btnColor || btnColor.trim() === '' 
            ? '' 
            : btnColor.startsWith('#') 
                ? btnColor 
                : `#${btnColor}`;
    params.append('custom_btn_hex', normalizedBtnColor);

    dispatch({ type: actions.UPDATE_ORG_CUSTOM_COLORS });

    axios
        .post(url, params, config)
        .then(({ data }) => {
            dispatch(updateLoggedInStatus(data));

            if (data.status === 'success') {
                dispatch(updateOrgCustomColorsSuccess(data.data));
                dispatch(
                    notificationMessage({
                        type: 'success',
                        textKey: 'Settings.organisation.colors.updateSuccess',
                    })
                );
                // Refresh account details to get updated colors
                dispatch(getEmployerAccountDetails());
            } else {
                const errorMessage =
                    data.message || data.errors || 'Settings.organisation.colors.updateError';
                dispatch(updateOrgCustomColorsFailed(errorMessage));
            }
        })
        .catch((error) => {
            dispatch(setNetworkError(error));
            dispatch(updateOrgCustomColorsFailed('Settings.organisation.colors.updateError'));
        });
};

const updateOrgCustomColorsSuccess = (colors) => ({
    type: actions.UPDATE_ORG_CUSTOM_COLORS_SUCCESS,
    payload: {
        custom_link_hex: colors.custom_link_hex,
        custom_btn_hex: colors.custom_btn_hex,
    },
});

const updateOrgCustomColorsFailed = (errorMessage) => (dispatch) => {
    dispatch(
        notificationMessage({
            type: 'error',
            textKey: typeof errorMessage === 'string' && errorMessage.startsWith('Settings.')
                ? errorMessage
                : 'Settings.organisation.colors.updateError',
            message: typeof errorMessage === 'string' && !errorMessage.startsWith('Settings.')
                ? errorMessage
                : undefined,
        })
    );
    dispatch({ type: actions.UPDATE_ORG_CUSTOM_COLORS_FAILED });
};
